import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import GuardRoute from "src/containers/GuardRoute/GuardRoute";
import {
	EditForms,
	FormDetail,
	Forms,
	NewObservationForm,
	EditObservationForm,
	ReportDetail,
	Reports
} from "src/pages";

import EditObservationQuestions from "src/pages/EditObservationQuestions/EditObservationQuestions";
import Locations from "src/features/Locations/pages/Locations/Locations";
import CreateRegion from "src/features/Locations/pages/CreateRegion/CreateRegion";
import EditRegion from "src/features/Locations/pages/EditRegion/EditRegion";
import CreateArea from "src/features/Locations/pages/CreateArea/CreateArea";
import EditArea from "src/features/Locations/pages/EditArea/EditArea";
import CreateStore from "src/features/Locations/pages/CreateStore/CreateStore";
import EditStore from "src/features/Locations/pages/EditStore/EditStore";

import "react-toastify/dist/ReactToastify.css";
import Home from "src/pages/Home/Home";
import CreateCarouselBanner from "src/features/HomepageCarouselBanners/pages/CreateCarouselBanner/CreateCarouselBanner";
import HomepageCarousel from "src/pages/Banners/HomepageCarousel";
import {
	CreateFullScreenTakeover,
	FullScreenTakeover
} from "src/features/FullScreenTakeover/pages";
import EditCarouselBanner from "src/features/HomepageCarouselBanners/pages/EditCarouselBanner/EditCarouselBanner";
import EditFullScreenTakeover from "src/features/FullScreenTakeover/pages/EditFullScreenTakeover/EditFullScreenTakeover";
import {
	CreatePromotion,
	EditPromotion,
	Promotion
} from "src/features/Promotion/pages";
import AppBranding from "src/features/AppBranding/pages/AppBranding/AppBranding";
import Analytics from "src/features/Analytics/pages/Analytics/Analytics";
import CreateAppBranding from "src/features/AppBranding/pages/CreateAppBranding/CreateAppBranding";
import EditAppBranding from "src/features/AppBranding/pages/EditAppBranding/EditAppBranding";
import QuickLinks from "src/features/QuickLinks/pages/QuickLinks/QuickLinks";
import CreateQuickLink from "src/features/QuickLinks/pages/CreateQuickLink/CreateQuickLink";
import EditQuickLink from "src/features/QuickLinks/pages/EditQuickLink/EditQuickLink";
import HomepageOrdering from "src/features/HomepageOrdering/pages/HomepageOrdering/HomepageOrdering";
import SectionTitles from "src/features/SectionTitles/pages/SectionTitles/SectionTitles";
import Navigation from "src/features/Navigation/pages/Navigation/Navigation";
import KeyShouts from "src/features/KeyShouts/pages/KeyShouts/KeyShouts";
import CreateKeyShout from "src/features/KeyShouts/pages/CreateKeyShout/CreateKeyShout";
import EditKeyShout from "src/features/KeyShouts/pages/EditKeyShout/EditKeyShout";
import Announcements from "src/features/Announcements/pages/Announcements/Announcements";
import CreateAnnouncement from "src/features/Announcements/pages/CreateAnnouncement/CreateAnnouncement";
import EditAnnouncement from "src/features/Announcements/pages/EditAnnouncement/EditAnnouncement";
import Events from "src/features/Events/pages/Events/Events";
import CreateEvent from "src/features/Events/pages/CreateEvent/CreateEvent";
import EditEvent from "src/features/Events/pages/EditEvent/EditEvent";
import AdminHashtags from "src/features/Hashtags/pages/Hashtags/Hashtags";
import CreateHashtag from "src/features/Hashtags/pages/CreateHashtag/CreateHashtag";
import EditHashtag from "src/features/Hashtags/pages/EditHashtag/EditHashtag";
import Groups from "src/features/Groups/pages/Groups/Groups";
import CreateGroup from "src/features/Groups/pages/CreateGroup/CreateGroup";
import EditGroup from "src/features/Groups/pages/EditGroup/EditGroup";
import People from "src/features/People/pages/People/People";
import PersonDetail from "src/features/People/pages/PersonDetail/PersonDetail";
import Permissions from "src/features/Permissions/pages/Permissions/Permissions";
import CreateRole from "src/features/Permissions/pages/CreateRole/CreateRole";
import EditRole from "src/features/Permissions/pages/EditRole/EditRole";
import FavouriteFeeds from "src/features/FavouriteFeeds/pages/FavouriteFeeds/FavouriteFeeds";
import AccessDenied from "src/pages/AccessDenied/AccessDenied";
import EstateReportDetail from "src/pages/EstateReportDetail/EstateReportDetail";
import ProtectedImageView from "src/features/observationsAdmin/pages/ProtectedImageView/ProtectedImageView";
import SocialFeedContent from "src/features/SocialFeedContent/pages/SocialFeedContent/SocialFeedContent";
import FormFinder from "src/pages/FormFinder/FormFinder";
import Error404 from "src/pages/Error404/Error404";
import { AppLayout } from "src/components/layout/AppLayout";

const RedirectToSubmissions = () => {
	const { formId } = useParams();
	const to = `/observations/submissions/${formId}`;

	return <Navigate to={to} replace />;
};

const AppTemplate = () => {
	return (
		<>
			<ToastContainer
				position="top-right"
				autoClose={5000}
				hideProgressBar={false}
				newestOnTop={true}
				closeOnClick={true}
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
				theme="light"
			/>

			<Routes>
				{/* ProtectedImageView bypasses layout (WebView only route) */}
				<Route element={<GuardRoute restrictedToEstateManager={true} />}>
					<Route
						path="/observations/image/:imageId"
						element={<ProtectedImageView />}
					/>
				</Route>

				{/* All other routes wrapped in AppLayout */}
				<Route element={<AppLayout />}>
					<Route path="/" element={<Home />} />

					<Route path="/access-denied" element={<AccessDenied />} />

					<Route path="/observations/submissions" element={<Forms />} />

					<Route
						path="/observations/submissions/:formId"
						element={<FormFinder />}
					/>

					<Route
						path="/observations/submissions/:formId/user/:userId/:observationId"
						element={<FormDetail />}
					/>

					<Route
						path="/observations/submissions/:formId/store/:storeId/:observationId"
						element={<FormDetail />}
					/>

					{/* Redirects from old routes to new */}
					{/* This is to handle the old routes that are hardcoded in other apps using legacy URL's */}
					<Route
						path="/observations/forms/:formId"
						element={<RedirectToSubmissions />}
					/>

					{/* Protected Routes */}
					<Route element={<GuardRoute restrictedToEstateManager={true} />}>
						<Route
							path="/observations/reports/estate/:formId"
							element={<EstateReportDetail />}
						/>
					</Route>

					<Route
						element={
							<GuardRoute requiredPermission="CanViewObservationReporting" />
						}
					>
						<Route path="/observations/reports" element={<Reports />} />

						<Route
							path="/observations/reports/form/:formId"
							element={<ReportDetail />}
						/>
						<Route
							path="/observations/reports/form/:formId/region/:selectedRegion"
							element={<ReportDetail />}
						/>
						<Route
							path="/observations/reports/form/:formId/region/:selectedRegion/area/:selectedArea"
							element={<ReportDetail />}
						/>
						<Route
							path="/observations/reports/form/:formId/region/:selectedRegion/area/:selectedArea/store/:selectedStore"
							element={<ReportDetail />}
						/>
					</Route>

					<Route
						element={
							<GuardRoute requiredPermission="CanViewEditObservationForms" />
						}
					>
						<Route path="/observations/forms" element={<EditForms />} />

						<Route
							path="/observations/forms/:formId/settings/"
							element={<EditObservationForm />}
						/>

						<Route
							path="/observations/forms/:formId/questions"
							element={<EditObservationQuestions />}
						/>

						<Route
							path="/observations/forms/new-observation-form"
							element={<NewObservationForm />}
						/>
					</Route>

					<Route
						element={<GuardRoute requiredPermission="CanViewSocialPosts" />}
					>
						<Route path="/social-feed" element={<SocialFeedContent />} />
					</Route>

					<Route element={<GuardRoute requiredPermission="CanViewAnalytics" />}>
						<Route path="/analytics" element={<Analytics />} />
					</Route>

					{/* Homepage Content (carousel, takeover, promotion, quick links, key shouts, ordering, branding) */}
					<Route
						element={
							<GuardRoute requiredPermission="CanViewEditHomepageContent" />
						}
					>
						<Route
							path="/vendor-sponsorship"
							element={
								<Navigate
									to="/vendor-sponsorship/homepage-carousel/banners?published=true"
									replace
								/>
							}
						/>
						<Route
							path="/vendor-sponsorship/homepage-carousel/banners/new-carousel-banner-form"
							element={<CreateCarouselBanner />}
						/>
						<Route
							path="/vendor-sponsorship/homepage-carousel/banners/new-carousel-banner-form/:formId"
							element={<EditCarouselBanner />}
						/>
						<Route
							path="/vendor-sponsorship/homepage-carousel/banners"
							element={<HomepageCarousel />}
						/>
						<Route
							path="/vendor-sponsorship/full-screen-takeover/banners"
							element={<FullScreenTakeover />}
						/>
						<Route
							path="/vendor-sponsorship/full-screen-takeover/banners/new-full-page-takeover-form"
							element={<CreateFullScreenTakeover />}
						/>
						<Route
							path="/vendor-sponsorship/full-screen-takeover/banners/new-full-page-takeover-form/:formId"
							element={<EditFullScreenTakeover />}
						/>
						<Route
							path="/vendor-sponsorship/promotion/banners"
							element={<Promotion />}
						/>
						<Route
							path="/vendor-sponsorship/promotion/banners/new-promotion-form"
							element={<CreatePromotion />}
						/>
						<Route
							path="/vendor-sponsorship/promotion/banners/new-promotion-form/:formId"
							element={<EditPromotion />}
						/>
						<Route path="/app/homepage/quick-links" element={<QuickLinks />} />
						<Route
							path="/app/homepage/quick-links/create"
							element={<CreateQuickLink />}
						/>
						<Route
							path="/app/homepage/quick-links/:quickLinkId"
							element={<EditQuickLink />}
						/>
						<Route
							path="/app/homepage/ordering"
							element={<HomepageOrdering />}
						/>
						<Route path="/app/homepage/key-shouts" element={<KeyShouts />} />
						<Route
							path="/app/homepage/key-shouts/create"
							element={<CreateKeyShout />}
						/>
						<Route
							path="/app/homepage/key-shouts/:keyShoutId"
							element={<EditKeyShout />}
						/>
						<Route
							path="/vendor-sponsorship/app-branding/brands"
							element={<AppBranding />}
						/>
						<Route
							path="/vendor-sponsorship/app-branding/brands/new-app-branding-form"
							element={<CreateAppBranding />}
						/>
						<Route
							path="/vendor-sponsorship/app-branding/brands/new-app-branding-form/:brandId"
							element={<EditAppBranding />}
						/>
					</Route>

					{/* Announcements */}
					<Route
						element={<GuardRoute requiredPermission="CanManageAnnouncements" />}
					>
						<Route path="/announcements" element={<Announcements />} />
						<Route
							path="/announcements/create"
							element={<CreateAnnouncement />}
						/>
						<Route
							path="/announcements/:announcementId"
							element={<EditAnnouncement />}
						/>
					</Route>

					{/* Events */}
					<Route
						element={<GuardRoute requiredPermission="CanViewEditEvents" />}
					>
						<Route path="/app/events" element={<Events />} />
						<Route path="/app/events/create" element={<CreateEvent />} />
						<Route path="/app/events/:eventId" element={<EditEvent />} />
					</Route>

					{/* Hashtags */}
					<Route
						element={<GuardRoute requiredPermission="CanManageHashtags" />}
					>
						<Route path="/social-feed/hashtags" element={<AdminHashtags />} />
						<Route
							path="/social-feed/hashtags/create"
							element={<CreateHashtag />}
						/>
						<Route
							path="/social-feed/hashtags/:hashtagId"
							element={<EditHashtag />}
						/>
					</Route>

					{/* Favourite Feeds */}
					<Route
						element={<GuardRoute requiredPermission="CanManageHashtags" />}
					>
						<Route
							path="/social-feed/favourite-feeds"
							element={<FavouriteFeeds />}
						/>
					</Route>

					{/* Section Titles */}
					<Route
						element={<GuardRoute requiredPermission="CanManageSettings" />}
					>
						<Route
							path="/app/homepage/section-titles"
							element={<SectionTitles />}
						/>
					</Route>

					{/* Navigation */}
					<Route
						element={<GuardRoute requiredPermission="CanViewEditNavigation" />}
					>
						<Route path="/app/navigation" element={<Navigation />} />
					</Route>

					{/* People */}
					<Route
						element={<GuardRoute requiredPermission="CanViewEditPeople" />}
					>
						<Route path="/users/people" element={<People />} />
						<Route path="/users/people/:personId" element={<PersonDetail />} />
					</Route>

					{/* Permissions */}
					<Route
						element={<GuardRoute requiredPermission="CanViewEditUserRoles" />}
					>
						<Route path="/users/permissions" element={<Permissions />} />
						<Route path="/users/permissions/create" element={<CreateRole />} />
						<Route path="/users/permissions/:roleId" element={<EditRole />} />
					</Route>

					{/* Groups */}
					<Route
						element={<GuardRoute requiredPermission="CanViewEditGroups" />}
					>
						<Route path="/users/groups" element={<Groups />} />
						<Route path="/users/groups/create" element={<CreateGroup />} />
						<Route path="/users/groups/:groupId" element={<EditGroup />} />
					</Route>

					{/* Locations */}
					<Route
						element={<GuardRoute requiredPermission="CanViewEditLocations" />}
					>
						<Route path="/users/locations" element={<Locations />} />
						<Route
							path="/users/locations/regions/create"
							element={<CreateRegion />}
						/>
						<Route
							path="/users/locations/regions/:regionId"
							element={<EditRegion />}
						/>
						<Route
							path="/users/locations/areas/create"
							element={<CreateArea />}
						/>
						<Route
							path="/users/locations/areas/:areaId"
							element={<EditArea />}
						/>
						<Route
							path="/users/locations/stores/create"
							element={<CreateStore />}
						/>
						<Route
							path="/users/locations/stores/:storeId"
							element={<EditStore />}
						/>
					</Route>

					<Route path="*" element={<Error404 />} />
				</Route>
			</Routes>
		</>
	);
};

export default AppTemplate;
